<template>
  <SideModal
    :title="`Convert ${fromCurrency}`"
    :showBackButton="false"
    @close="$emit('close-modal')"
  >
    <template #content>
      <div class="convert-form">
        <!-- From Section -->
        <div class="convert-form__section">
          <label class="convert-form__label">FROM</label>
          <div class="convert-form__currency-display">
            <div class="convert-form__currency-info">
              <CurrencyFlag :currency="fromCurrency" />
              <span class="convert-form__currency-symbol">{{ getCurrencySymbol(fromCurrency) }}</span>
              <span class="convert-form__currency-code">{{ fromCurrency }}</span>
            </div>
            <span class="convert-form__balance">
              Balance: {{ formatAmountToDollar(balance, 2, fromCurrency) }}
            </span>
          </div>
          <div class="convert-form__input-wrapper">
            <input
              type="text"
              :value="displayAmount"
              @input="handleAmountInput"
              class="convert-form__input"
              placeholder="0.00"
            />
            <button
              class="convert-form__max-btn"
              @click="setMaxAmount"
            >
              MAX
            </button>
          </div>
        </div>

        <!-- Swap Divider -->
        <div class="convert-form__divider">
          <div class="convert-form__divider-line"></div>
          <div class="convert-form__swap-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 7H16M16 7L13 4M16 7L13 10M16 13H4M4 13L7 10M4 13L7 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="convert-form__divider-line"></div>
        </div>

        <!-- To Section -->
        <div class="convert-form__section">
          <label class="convert-form__label">TO</label>
          <div class="convert-form__wallet-options">
            <button
              v-for="wallet in availableWallets"
              :key="wallet.currency"
              class="convert-form__wallet-option"
              :class="{ 'convert-form__wallet-option--selected': toCurrency === wallet.currency }"
              @click="selectToCurrency(wallet.currency)"
            >
              <CurrencyFlag :currency="wallet.currency" />
              <span>{{ getCurrencySymbol(wallet.currency) }} {{ wallet.currency }}</span>
            </button>
          </div>
        </div>

        <!-- Converted Amount -->
        <div v-if="toCurrency && amount > 0" class="convert-form__result">
          <p class="convert-form__result-label">You'll receive approximately</p>
          <h3 class="convert-form__result-amount">
            {{ formatAmountToDollar(convertedAmount, 2, toCurrency) }}
          </h3>
        </div>

        <!-- Rate Info -->
        <div v-if="toCurrency" class="convert-form__rate-info">
          <div class="convert-form__rate-row">
            <span>Exchange Rate</span>
            <span>1 {{ getCurrencySymbol(fromCurrency) }} = {{ exchangeRate.toFixed(4) }} {{ getCurrencySymbol(toCurrency) }}</span>
          </div>
          <div class="convert-form__rate-row">
            <span>Conversion Fee</span>
            <span>{{ formatAmountToDollar(conversionFee, 2, fromCurrency) }}</span>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="convert-form__error">
          {{ errorMessage }}
        </div>

        <!-- Convert Button -->
        <button
          class="convert-form__submit-btn"
          :disabled="!canConvert || loading"
          @click="handleConvert"
        >
          <SmallLoader v-if="loading" />
          <span v-else>Convert {{ fromCurrency }} to {{ toCurrency || '...' }}</span>
        </button>
      </div>
    </template>
  </SideModal>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import SideModal from "@/components/general/sidemodal/SideModal.vue";
import CurrencyFlag from "@/components/wallet/CurrencyFlag.vue";
import { formatAmountToDollar, getCurrencySymbol, convertToLocale } from "@/utils/helpers.js";
import api from "@/utils/api.js";
import responseHandler from "@/utils/responseHandler.js";

const props = defineProps({
  fromCurrency: {
    type: String,
    required: true,
  },
  balance: {
    type: Number,
    default: 0,
  },
  wallets: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["close-modal"]);

const store = useStore();

const amount = ref(0);
const toCurrency = ref("");
const exchangeRate = ref(1);
const loading = ref(false);
const errorMessage = ref("");

const displayAmount = computed(() => {
  return amount.value ? convertToLocale(amount.value) : "";
});

const handleAmountInput = (event) => {
  const rawValue = event.target.value.replace(/,/g, "");
  const numValue = parseFloat(rawValue) || 0;
  amount.value = numValue;
};

const availableWallets = computed(() => {
  return props.wallets.filter(w => w.currency !== props.fromCurrency);
});

const conversionFee = computed(() => {
  // 0.5% conversion fee
  return amount.value * 0.005;
});

const convertedAmount = computed(() => {
  const netAmount = amount.value - conversionFee.value;
  return netAmount * exchangeRate.value;
});

const canConvert = computed(() => {
  return (
    amount.value > 0 &&
    amount.value <= props.balance &&
    toCurrency.value &&
    toCurrency.value !== props.fromCurrency
  );
});

const setMaxAmount = () => {
  amount.value = props.balance;
};

const selectToCurrency = async (currency) => {
  toCurrency.value = currency;
  await fetchExchangeRate();
};

const fetchExchangeRate = async () => {
  if (!toCurrency.value) return;
  
  try {
    const query = {
      from: props.fromCurrency,
      to: toCurrency.value,
      amount: 1,
    };
    await store.dispatch("organisationModule/convertCurrency", query);
    exchangeRate.value = store.getters["organisationModule/currencyRate"] || 1;
  } catch (error) {
    console.error("Error fetching exchange rate:", error);
    exchangeRate.value = 1;
  }
};

const handleConvert = async () => {
  if (!canConvert.value) return;
  
  loading.value = true;
  errorMessage.value = "";
  
  try {
    const payload = {
      amount: amount.value,
      from: props.fromCurrency,
      to: toCurrency.value,
    };
    
    const response = await store.dispatch("organisationModule/convertWallet", payload);
    
    if (response) {
      store.dispatch("showToast", {
        description: response.data?.message || "Conversion successful!",
        display: true,
        type: "success",
      });
      emit("close-modal");
    }
  } catch (error) {
    errorMessage.value = error.response?.data?.message || "Conversion failed. Please try again.";
    responseHandler.handleError(error);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  if (availableWallets.value.length > 0) {
    selectToCurrency(availableWallets.value[0].currency);
  }
});
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$btn-primary: #001240;
$btn-primary-hover: #06206B;
$accent-primary: #6366F1;
$accent-light: #EEF2FF;
$error: #DC2626;
$error-light: #FEF2F2;

.convert-form {
  &__section {
    margin-bottom: 24px;
  }

  &__label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: $text-muted;
    margin-bottom: 12px;
  }

  &__currency-display {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: $bg-subtle;
    border-radius: 10px;
    margin-bottom: 12px;
  }

  &__currency-info {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__currency-symbol {
    font-size: 16px;
    font-weight: 600;
    color: $text-primary;
  }

  &__currency-code {
    font-size: 14px;
    color: $text-secondary;
  }

  &__balance {
    font-size: 13px;
    color: $text-muted;
  }

  &__input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__input {
    width: 100%;
    height: 56px;
    padding: 0 70px 0 16px;
    border: 1px solid $border-color;
    border-radius: 10px;
    font-size: 24px;
    font-weight: 500;
    color: $text-primary;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: $accent-primary;
    }

    &::placeholder {
      color: $border-color;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  &__max-btn {
    position: absolute;
    right: 12px;
    padding: 6px 12px;
    background: $accent-light;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    color: $accent-primary;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: $accent-primary;
      color: white;
    }
  }

  &__divider {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 24px 0;
  }

  &__divider-line {
    flex: 1;
    height: 1px;
    background: $border-color;
  }

  &__swap-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $bg-subtle;
    border: 1px solid $border-color;
    border-radius: 50%;
    color: $text-secondary;
  }

  &__wallet-options {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__wallet-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: $bg-subtle;
    border: 1px solid transparent;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    color: $text-secondary;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: $accent-light;
      border-color: $accent-primary;
    }

    &--selected {
      background: $accent-light;
      border-color: $accent-primary;
      color: $accent-primary;
    }
  }

  &__result {
    text-align: center;
    padding: 20px;
    background: $bg-subtle;
    border-radius: 10px;
    margin: 24px 0;
  }

  &__result-label {
    font-size: 13px;
    color: $text-muted;
    margin: 0 0 8px;
  }

  &__result-amount {
    font-size: 24px;
    font-weight: 600;
    color: $text-primary;
    margin: 0;
  }

  &__rate-info {
    background: $bg-subtle;
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 24px;
  }

  &__rate-row {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    color: $text-secondary;
    padding: 8px 0;

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
    }

    &:not(:last-child) {
      border-bottom: 1px solid $border-color;
    }

    span:last-child {
      font-weight: 500;
      color: $text-primary;
    }
  }

  &__error {
    background: $error-light;
    color: $error;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 13px;
    margin-bottom: 16px;
  }

  &__submit-btn {
    width: 100%;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $btn-primary;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover:not(:disabled) {
      background: $btn-primary-hover;
    }

    &:disabled {
      background: $border-color;
      color: $text-muted;
      cursor: not-allowed;
    }
  }
}
</style>
